import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import styled from "styled-components";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getProducts } from "../../../apis/products";
import { getDrugMovementReport } from "../../../apis/customTemplate";
import { toastify } from "../../../helperFunctions/toastify";
import DateInput from "../../SharedComponents/DateInput";
import SelectComponent from "../../SharedComponents/SelectComponent";
import { setTableSearch } from "../../../redux/slices/commonSlice";

import {
  customDateSort,
  formatDate,
  downloadCsv,
} from "../../../helperFunctions/utils";
import MultiSelectComponent from "../../SharedComponents/MultiSelctCompoenent";

const FilterWrapper = styled.div`
  label {
    color: inherit;
  }

  .btn-wrapper {
    align-items: center;
    display: flex;
    margin-top: 20px;
  }
`;

function DrugMovementReport() {
  const dispatch = useDispatch();
  const location = useLocation();
  const buttonRef = useRef(null);
  const pathname = location.pathname;
  const fieldPathname = pathname.replace("/", "");

  const { tableSearch } = useSelector((state) => state.commonData);
  const [drugs, setDrugs] = useState([]);
  const [selectedDrug, setSelectedDrug] = useState(tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.selectedDrug ? tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.selectedDrug : "");
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState(tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.startDate ? tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.startDate : new Date());
  const [endDate, setEndDate] = useState(tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.endDate ? tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.endDate : new Date());

  const onReduxFilter = (valueStartDate, valueEndDate, valueDrug) => {
    dispatch(
      setTableSearch({
        ...tableSearch,
        [fieldPathname]: {
          ...tableSearch[fieldPathname],
          [tableSearch[fieldPathname]?.currentState]: {
            startDate: valueStartDate,
            endDate: valueEndDate,
            selectedDrug: valueDrug,
          },
        },
      })
    );
  };

  useEffect(() => {
    if (tableSearch[fieldPathname]?.currentState) {
      setStartDate(tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.startDate ? tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.startDate : new Date());
      setEndDate(tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.endDate ? tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.endDate : new Date());
      setSelectedDrug(tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.currentState]?.selectedDrug ? tableSearch[fieldPathname]?.[tableSearch[fieldPathname]?.selectedReport?.currentState]?.selectedDrug : "");

      if (buttonRef.current) {
        setTimeout(() => {
          if (buttonRef.current) {
            buttonRef.current.click();
          }
        }, [1000])
      }
    }
  }, [tableSearch[fieldPathname]?.selectedReport?._id]);

  useEffect(() => {
    getProducts().then((res) => {
      setDrugs(res.filter(x => x.type?._id === tableSearch[fieldPathname]?.selectedReport?._id));
    });
  }, [tableSearch[fieldPathname]?.selectedReport?._id]);

  const getDataMovementReport = () => {
    if (startDate !== null && endDate !== null) {
      getDrugMovementReport({
        startDate: startDate,
        endDate: endDate,
        drug: selectedDrug,
        category: tableSearch[fieldPathname]?.selectedReport?._id
      }).then((res) => {
        if (res?.result?.data) {
          setFilteredData(res.result.data);
        }
      });
    }
  }

  const columns = [
    {
      name: "Date",
      selector: (row) => formatDate(row.date),
      width: "140px",
      sortable: true,
      sortFunction: (a, b) => customDateSort(a, b, "date"),
    },
    {
      name: "Drug",
      selector: (row) => row.name,
    },
    {
      name: "From",
      selector: (row) => row.from,
    },
    {
      name: "Added Qty",
      selector: (row) => row.qty,
    },
    {
      name: "Remaining Qty",
      selector: (row) => row.remaining_qty,
    },
    {
      name: "Created By",
      selector: (row) => row.createdBy,
    },
    {
      name: "Remark",
      selector: (row) => row.remark,
    },
  ];


  const remainingMap = {}; // Tracks latest remaining_qty per drug
  const lastQtyMap = {}; // Tracks last used qty per drug
  const initialQuantities = {}; // Maps drug._id => qty
  const firstSeen = new Set(); // Tracks first occurrence of drug

  // Build initial qty map
  drugs.forEach((drug) => {
    if (drug?._id) {
      initialQuantities[drug._id.toString()] = drug.qty ?? 0;
    }
  });

  const tableData = useMemo(() => {
    if (!filteredData) {
      return [];
    }

    return filteredData?.map((e, index) => {
      const productId = e.id?.toString(); // Adjust if needed

      if (!productId) {
        return { ...e, index, key: index, remaining_qty: 0 };
      }

      let newRemaining;

      if (!firstSeen.has(productId)) {
        // First record: set initial qty directly
        newRemaining = initialQuantities[productId] ?? 0;
        firstSeen.add(productId);
      } else {
        // Subsequent: subtract previous record's qty
        const prevRemaining = remainingMap[productId] ?? 0;
        const prevQty = lastQtyMap[productId] ?? 0;
        newRemaining = prevRemaining - prevQty;
      }

      // Update trackers
      remainingMap[productId] = newRemaining;
      lastQtyMap[productId] = e.qty ?? 0;

      return {
        ...e,
        index,
        key: `${e?.date}-${index}`,
        remaining_qty: newRemaining,
      };
    });
  }, [filteredData]);

  // const data = filteredData?.map((e, index) => {
  //   return {
  //     ...e,
  //     index,
  //     key: index,
  //   };
  // });

  const customStyles = {
    headCells: {
      style: {
        justifyContent: "center",
      },
    },
    cells: {
      style: {
        justifyContent: "center",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row) => row,
      style: {
        borderWidth: "0px",
      },
    },
    {
      when: (row) => row.index % 2 === 1,
      style: {
        backgroundColor: "#f8f8f8",
        borderWidth: "0px",
      },
    },
  ];

  const exportCsv = () => {
    const csvData = tableData.map((row) => {
      return {
        Date: formatDate(row.date),
        From: row.from,
        "Added Qty": row.qty,
        "Created By": row.createdBy,
        Remark: row.remark,
      };
    });

    downloadCsv(csvData, "Drug Movement Report");
  };

  return (
    <div className="p-0 table-cell-whitespace">
      <div
        className="mainHeading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          paddingTop: "0px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <FilterWrapper className="d-flex">
            <Form.Group className="me-3" style={{ width: "260px" }}>
              <Form.Label className="headings2" style={{ color: "#000" }}>
                Filter by Product
              </Form.Label>
              <MultiSelectComponent
                searchEnabled
                array={selectedDrug}
                options={
                  drugs?.length > 0
                    ? [
                      ...drugs.map((item) => {
                        return {
                          label: item.name,
                          value: item._id,
                        };
                      }),
                    ]
                    : []
                }
                onChange={(e) => {
                  setSelectedDrug(e);
                }}
              />
            </Form.Group>
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={startDate}
                label="Start Date"
                placeholder="Select"
                onChange={(e) => {
                  setStartDate(e);
                }}
              />
            </div>
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={endDate}
                label="End Date"
                placeholder="Select"
                onChange={(e) => {
                  setEndDate(e);
                }}
              />
            </div>
            <div className="btn-wrapper">
              <Button
                ref={buttonRef}
                className="addItem me-3"
                onClick={() => {
                  if (endDate < startDate) {
                    toastify("End date is less than start date", "error");
                    return;
                  }
                  onReduxFilter(startDate, endDate, selectedDrug);
                  getDataMovementReport();
                }}
              >
                Search
              </Button>

              <Button className="addItem" onClick={() => exportCsv()}>
                Export
              </Button>
            </div>
          </FilterWrapper>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={tableData}
        pagination
        customStyles={customStyles}
        conditionalRowStyles={conditionalRowStyles}
        keyField="key"
      />
    </div>
  );
}

export default DrugMovementReport;
